import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './useAuth';

export interface WalletBreakdown {
  totalBalance: number;
  usedMargin: number;
  futuresUsedMargin: number;
  propUsedMargin: number;
  futuresOrdersReserved: number;
  propOrdersReserved: number;
  unrealizedPnl: number;
  availableBalance: number;
  robotAllocatedBalance: number;
  stakedAmount: number;
  loading: boolean;
  error: string | null;
}

export const useWalletBreakdown = () => {
  const { user } = useAuth();
  const [breakdown, setBreakdown] = useState<WalletBreakdown>({
    totalBalance: 0,
    usedMargin: 0,
    futuresUsedMargin: 0,
    propUsedMargin: 0,
    futuresOrdersReserved: 0,
    propOrdersReserved: 0,
    unrealizedPnl: 0,
    availableBalance: 0,
    robotAllocatedBalance: 0,
    stakedAmount: 0,
    loading: true,
    error: null
  });

  const calculateBreakdown = useCallback(async () => {
    if (!user) {
      setBreakdown(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setBreakdown(prev => ({ ...prev, loading: true, error: null }));

      // Get user balances
      const { data: balanceData, error: balanceError } = await supabase
        .from('balances')
        .select('usdt_balance, btc_balance')
        .eq('user_id', user.id)
        .single();

      if (balanceError) throw balanceError;

      const usdtBalance = parseFloat(balanceData?.usdt_balance?.toString() || '0') || 0;
      const btcBalance = parseFloat(balanceData?.btc_balance?.toString() || '0') || 0;

      // Get current BTC price
      const { data: btcPriceData, error: btcPriceError } = await supabase
        .from('market_data')
        .select('price')
        .eq('symbol', 'BTCUSDT')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();

      if (btcPriceError) throw btcPriceError;

      const currentBtcPrice = parseFloat(btcPriceData?.price?.toString() || '0') || 0;

      // Calculate total balance from USDT and BTC
      let totalBalance = usdtBalance + (btcBalance * currentBtcPrice);

      // Add other user assets to total balance
      const { data: userAssets, error: assetsError } = await supabase
        .from('user_assets')
        .select('asset_symbol, balance')
        .eq('user_id', user.id);

      if (!assetsError && userAssets && userAssets.length > 0) {
        // Get market data for all assets
        const { data: marketData, error: marketError } = await supabase
          .from('market_data')
          .select('symbol, price');

        if (!marketError && marketData) {
          for (const asset of userAssets) {
            if (asset.asset_symbol === 'USDT' || asset.asset_symbol === 'BTC') {
              continue; // Already counted above
            }
            
            // Find market data for this asset
            const assetMarketData = marketData.find(data => data.symbol === `${asset.asset_symbol}USDT`);
            const price = parseFloat(assetMarketData?.price?.toString() || '0') || 0;
            const balance = parseFloat(asset.balance?.toString() || '0') || 0;
            
            if (price > 0 && balance > 0) {
              totalBalance += balance * price;
            }
          }
        }
      }

      // Get futures positions margin and PnL
      const { data: futuresPositions, error: futuresError } = await supabase
        .from('futures_positions')
        .select('margin, unrealized_pnl')
        .eq('user_id', user.id)
        .eq('is_open', true);

      if (futuresError) throw futuresError;

      // Get prop positions margin and PnL
      const { data: propPositions, error: propError } = await supabase
        .from('prop_positions')
        .select('margin, unrealized_pnl')
        .eq('user_id', user.id)
        .eq('is_open', true);

      if (propError) throw propError;

      // Get futures orders reserved margin
      const { data: futuresOrders, error: futuresOrdersError } = await supabase
        .from('futures_orders')
        .select('reserved_margin')
        .eq('user_id', user.id)
        .eq('status', 'open');

      if (futuresOrdersError) throw futuresOrdersError;

      // Get prop orders reserved margin
      const { data: propOrders, error: propOrdersError } = await supabase
        .from('prop_orders')
        .select('reserved_margin')
        .eq('user_id', user.id)
        .eq('status', 'open');

      if (propOrdersError) throw propOrdersError;

      // Get robot state
      const { data: robotState, error: robotError } = await supabase
        .from('robot_states')
        .select('allocated_balance')
        .eq('user_id', user.id)
        .single();

      if (robotError && robotError.code !== 'PGRST116') throw robotError;

      // Get user stakes
      const { data: userStakes, error: stakesError } = await supabase
        .from('user_stakes')
        .select('asset_symbol, staked_amount')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (stakesError) throw stakesError;

      // Calculate individual margin components
      const futuresUsedMargin = futuresPositions?.reduce((sum, pos) => {
        const margin = parseFloat(pos.margin?.toString() || '0') || 0;
        return sum + margin;
      }, 0) || 0;

      const propUsedMargin = propPositions?.reduce((sum, pos) => {
        const margin = parseFloat(pos.margin?.toString() || '0') || 0;
        return sum + margin;
      }, 0) || 0;

      const futuresOrdersReserved = futuresOrders?.reduce((sum, order) => {
        const reserved = parseFloat(order.reserved_margin?.toString() || '0') || 0;
        return sum + reserved;
      }, 0) || 0;

      const propOrdersReserved = propOrders?.reduce((sum, order) => {
        const reserved = parseFloat(order.reserved_margin?.toString() || '0') || 0;
        return sum + reserved;
      }, 0) || 0;

      // Calculate total used margin
      const usedMargin = futuresUsedMargin + propUsedMargin + futuresOrdersReserved + propOrdersReserved;

      // Calculate unrealized PnL correctly without extra leverage multiplication
      const futuresUnrealizedPnl = futuresPositions?.reduce((sum, pos) => {
        const unrealizedPnl = parseFloat(pos.unrealized_pnl?.toString() || '0') || 0;
        return sum + unrealizedPnl;
      }, 0) || 0;

      const propUnrealizedPnl = propPositions?.reduce((sum, pos) => {
        const unrealizedPnl = parseFloat(pos.unrealized_pnl?.toString() || '0') || 0;
        return sum + unrealizedPnl;
      }, 0) || 0;

      const unrealizedPnl = futuresUnrealizedPnl + propUnrealizedPnl;

      // Get robot allocated balance
      const robotAllocatedBalance = parseFloat(robotState?.allocated_balance?.toString() || '0') || 0;

      // Calculate total staked value in USDT
      let totalStakedValue = 0;
      if (userStakes && userStakes.length > 0) {
        for (const stake of userStakes) {
          const stakedAmount = parseFloat(stake.staked_amount?.toString() || '0') || 0;
          
          if (stake.asset_symbol === 'USDT') {
            totalStakedValue += stakedAmount;
          } else if (stake.asset_symbol === 'BTC') {
            totalStakedValue += stakedAmount * currentBtcPrice;
          } else {
            // For other assets, get their current price from market_data
            const { data: assetPrice, error: assetPriceError } = await supabase
              .from('market_data')
              .select('price')
              .eq('symbol', `${stake.asset_symbol}USDT`)
              .order('timestamp', { ascending: false })
              .limit(1)
              .single();
            
            if (!assetPriceError && assetPrice) {
              const price = parseFloat(assetPrice.price?.toString() || '0') || 0;
              if (price > 0) {
                totalStakedValue += stakedAmount * price;
              }
            }
          }
        }
      }

      // Calculate available balance
      const availableBalance = Math.max(0, totalBalance + unrealizedPnl - usedMargin - robotAllocatedBalance - totalStakedValue);

      setBreakdown({
        totalBalance,
        usedMargin,
        futuresUsedMargin,
        propUsedMargin,
        futuresOrdersReserved,
        propOrdersReserved,
        unrealizedPnl,
        availableBalance,
        robotAllocatedBalance,
        stakedAmount: totalStakedValue,
        loading: false,
        error: null
      });

    } catch (error: any) {
      console.error('Error calculating wallet breakdown:', error);
      setBreakdown(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to calculate wallet breakdown'
      }));
    }
  }, [user]);

  // Recalculate breakdown when dependencies change
  useEffect(() => {
    calculateBreakdown();
  }, [calculateBreakdown]);

  // Refresh breakdown function
  const refreshBreakdown = useCallback(() => {
    calculateBreakdown();
  }, [calculateBreakdown]);

  return {
    ...breakdown,
    refreshBreakdown
  };
};